import { CSSProperties } from 'react';
import * as React from 'react';
import {
  TableRow,
  Table,
  TableHeaderCell,
  TableCell,
  TableSortIcon,
  Tooltip,
  TooltipTrigger,
} from '@spotify-internal/encore-web';
import Toggle from 'components/Toggle';
import { Viewport, useBreakpointValue, breakpointValues } from 'libs/utils/useViewport';
import styled from 'styled-components';
import { useT } from '@spotify-internal/i18n-core';

export const PAGE_SIZE = 100;

export type WorkTableColumnKey = 'index' | 'title' | 'writers' | 'recordings' | 'totalStreamCount';

export const defaultColumns: ColumnDefinition<any, WorkTableColumnKey>[] = [
  {
    key: 'index',
    title: '',
    width: '36px',
    minViewport: Viewport.SM,
  },
  {
    key: 'title',
    title: 'Title',
    width: '240px',
    truncate: true,
  },
  {
    key: 'writers',
    title: 'Writers',
    width: '*',
    truncate: true,
    minViewport: Viewport.MD,
  },
  {
    key: 'recordings',
    title: 'Recordings',
    width: '160px',
    align: 'end',
    truncate: true,
    minViewport: Viewport.SM,
  },
  {
    key: 'totalStreamCount',
    title: 'Streams last 28 Days',
    width: '220px',
    align: 'end',
    numerical: true,
    sortable: true,
  },
];

type SortOrder = 'ASC' | 'DESC';

const ColumnHeader = styled.div<{ currentlySelected: boolean }>`
  font-weight: 700;
  color: ${(props) => (props.currentlySelected ? 'black' : 'inherit')};
`;

const ClickableHeader = styled.button`
  border: none;
  background-color: inherit;
  color: inherit;
  font-size: 11px;
  letter-spacing: inherit;
  line-height: inherit;
  padding: 0;
  text-align: right;

  &:hover {
    text-decoration: underline;
  }
`;

export type TooltipPositions = React.ComponentProps<typeof TooltipTrigger>['placement'];

export type ColumnDefinition<Item, KeyType = string> = {
  key: KeyType;
  title?: React.ReactNode;
  width?: string;
  align?: 'start' | 'end' | 'center';
  condensed?: boolean;
  sortable?: boolean;
  highlight?: boolean;
  truncate?: boolean;
  numerical?: boolean;
  multiline?: boolean;
  tooltip?: string;
  tooltipPlacement?: TooltipPositions;
  minViewport?: Viewport;
  render?: (item: Item) => React.ReactNode;
};

type Props<Item, ColumnKey> = {
  columns: ColumnDefinition<Item, ColumnKey>[];
  items: Item[];
  isRowSelected?: (item: Item, itemIndex: number) => boolean;
  keyForItem: (item: Item, itemIndex: number) => string;
  renderCell?: (
    item: Item,
    itemIndex: number,
    column: ColumnDefinition<Item, ColumnKey>,
  ) => React.ReactNode;
  rowStyles?: (item: Item) => CSSProperties;
  rowTooltip?: boolean;
  onRowClick?: (item: Item, itemIndex: number, e: React.MouseEvent) => void;
  onSortChanged?: (sortKey: ColumnKey, direction: 'ASC' | 'DESC') => void;
  selectable?: (item: Item, itemIndex: number) => boolean;
  sortKey?: ColumnKey;
  sortOrder?: SortOrder;
  style?: React.CSSProperties;
  memoizeRows?: boolean;
  testId?: string;
  hover?: boolean;
};

// position: relative doesn't work on <tr> elements, except for when transform: scale(1) is added. It is unclear why this is: https://stackoverflow.com/questions/8501727/table-row-wont-contain-elements-with-positionabsolute
const HoverableTableRow = styled<any>(TableRow)`
  ${(props) =>
    props.rowTooltip
      ? `
      position: relative;

      &:hover ${RowTooltip} {
        display: inline;
      }`
      : ''}
`;

const MemoizedTableRow = React.memo(HoverableTableRow, (prevProps, nextProps) => {
  return prevProps.key === nextProps.key;
});

export const RowTooltip = styled(Tooltip)`
  position: absolute;
  display: none;
`;

export default function DataTable<Item, ColumnKey>({
  columns: allColumns,
  items,
  isRowSelected,
  keyForItem,
  onSortChanged,
  onRowClick,
  selectable,
  rowStyles,
  rowTooltip,
  renderCell,
  sortKey,
  sortOrder,
  style,
  memoizeRows,
  testId,
  hover = true,
}: Props<Item, ColumnKey>) {
  const t = useT();

  const currentScreenSize = useBreakpointValue();
  const columns = allColumns.filter((col) => {
    if (!col.minViewport) {
      return true;
    } else {
      return breakpointValues[col.minViewport] <= currentScreenSize;
    }
  });

  const RowComponent = memoizeRows ? MemoizedTableRow : HoverableTableRow;

  return (
    <Table style={style} data-testid={testId}>
      <colgroup>
        {columns.map((column) => (
          <col key={String(column.key)} style={{ width: column.width }} />
        ))}
      </colgroup>
      <thead>
        <TableRow>
          {columns.map((column) => {
            const isCurrentSortKey = column.key === sortKey;
            const columnText = (
              <ColumnHeader currentlySelected={isCurrentSortKey}>
                {column.sortable && (
                  <TableSortIcon
                    direction={isCurrentSortKey ? (sortOrder === 'DESC' ? 'down' : 'up') : 'down'}
                  />
                )}
                {column.title}
              </ColumnHeader>
            );

            const columnHeader = column.sortable ? (
              <ClickableHeader
                onClick={() => {
                  if (!onSortChanged) {
                    return;
                  }
                  onSortChanged(
                    column.key,
                    !isCurrentSortKey ? 'DESC' : sortOrder === 'DESC' ? 'ASC' : 'DESC',
                  );
                }}
                title={
                  sortOrder === 'DESC'
                    ? t(
                        'H_DATATABLE_0',
                        'Sort in descending order',
                        'Text that can be clicked to sort a table in descending order',
                      )
                    : t(
                        'H_DATATABLE_1',
                        'Sort in ascending order',
                        'Text that can be clicked to sort a table in ascending order',
                      )
                }
              >
                {columnText}
              </ClickableHeader>
            ) : (
              columnText
            );

            return (
              <TableHeaderCell
                style={{ verticalAlign: 'middle' }}
                role="columnheader"
                aria-sort={
                  !column.sortable ? 'none' : sortOrder === 'DESC' ? 'descending' : 'ascending'
                }
                data-testid={column.key}
                key={String(column.key)}
                active={column.key === sortKey}
                align={column.align}
              >
                {column.tooltip ? (
                  <Toggle>
                    {({ isActive, activate, deactivate }) => (
                      <TooltipTrigger
                        overlay={
                          isActive && (
                            <Tooltip style={{ textTransform: 'none', maxWidth: '245px' }}>
                              {column.tooltip}
                            </Tooltip>
                          )
                        }
                        placement={column.tooltipPlacement || 'top'}
                        onShow={activate}
                        onHide={deactivate}
                        style={{
                          textAlign: column.align,
                        }}
                      >
                        {columnHeader}
                      </TooltipTrigger>
                    )}
                  </Toggle>
                ) : (
                  columnHeader
                )}
              </TableHeaderCell>
            );
          })}
        </TableRow>
      </thead>
      <tbody>
        {items &&
          items.map((item, index) => (
            <RowComponent
              key={keyForItem(item, index)}
              style={rowStyles ? rowStyles(item) : undefined}
              rowTooltip={rowTooltip}
              hover={(selectable ? selectable(item, index) : false) || hover}
              select={isRowSelected ? isRowSelected(item, index) : undefined}
              onClick={(e: React.MouseEvent) => {
                if (onRowClick) {
                  onRowClick(item, index, e);
                }
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={String(column.key)}
                  align={column.align}
                  numerical={column.numerical}
                  truncate={column.truncate}
                  highlight={column.highlight}
                  condensed={column.condensed}
                  role="cell"
                >
                  {column.render
                    ? column.render(item)
                    : renderCell && renderCell(item, index, column)}
                </TableCell>
              ))}
            </RowComponent>
          ))}
      </tbody>
    </Table>
  );
}
